import React, { useState } from 'react';
import { useAuth } from '../AuthContext'; // Adjust path as needed
import { useToast } from '../Toast'; // Adjust path as needed
import { Button } from './ui/button';
import LanguageToggle from './ui/LanguageToggle';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  User, 
  LogOut, 
  Settings, 
  Shield, 
  Clock, 
  CheckCircle,
  XCircle,
  ChevronDown,
  Menu,
  X
} from 'lucide-react';

// Utility function for class names
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

// User Profile Dropdown Component - Updated for transparent background
const UserProfileDropdown = ({ user, onLogout, isLoggingOut }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const getUserRole = () => {
    return user.user_type.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  const isQualified = () => {
    if (user.user_type === 'OPERATOR' && user.operator_profile) {
      return user.operator_profile.is_qualified || false;
    }
    return true; // Admins are considered qualified
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-white/90 hover:bg-white text-gray-800 shadow-lg border"
        disabled={isLoggingOut}
      >
        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
          <User className="w-4 h-4 text-white" />
        </div>
        <div className="hidden sm:block text-left">
          <p className="text-sm font-medium text-gray-800">
            {user.first_name}
          </p>
          <p className="text-xs text-gray-600">{getUserRole()}</p>
        </div>
        <ChevronDown className={cn(
          "w-4 h-4 text-gray-600 transition-transform duration-200",
          isOpen && "rotate-180"
        )} />
      </Button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown Menu */}
          <Card className="absolute top-full right-0 mt-2 w-80 shadow-lg border-0 z-50 animate-[slideDown_0.2s_ease-out] mb-4">
            <CardContent className="p-4">
              {/* User Info */}
              <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {user.first_name} 
                  </h3>
                  <p className="text-sm text-gray-500 truncate">@{user.username}</p>
                  {/* <p className="text-xs text-gray-400">{user.email}</p> */}
                </div>
              </div>

              {/* User Details */}
              <div className="py-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Role</span>
                  <div className="flex items-center gap-1">
                    <Shield className="w-3 h-3 text-blue-600" />
                    <span className="text-sm font-medium text-gray-900">{getUserRole()}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status</span>
                  <div className="flex items-center gap-1">
                    {user.is_active ? (
                      <CheckCircle className="w-3 h-3 text-green-600" />
                    ) : (
                      <XCircle className="w-3 h-3 text-red-600" />
                    )}
                    <span className={cn(
                      "text-sm font-medium",
                      user.is_active ? "text-green-600" : "text-red-600"
                    )}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {user.user_type === 'OPERATOR' && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Qualified</span>
                    <div className="flex items-center gap-1">
                      {isQualified() ? (
                        <CheckCircle className="w-3 h-3 text-green-600" />
                      ) : (
                        <XCircle className="w-3 h-3 text-yellow-600" />
                      )}
                      <span className={cn(
                        "text-sm font-medium",
                        isQualified() ? "text-green-600" : "text-yellow-600"
                      )}>
                        {isQualified() ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                )}

                {user.last_login && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Last Login</span>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {new Date(user.last_login).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Role-specific Details */}
              {user.user_type === 'OPERATOR' && user.operator_profile && (
                <div className="py-3 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Operator Details</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Code:</span>
                      <span className="text-gray-900">{user.operator_profile.operator_code}</span>
                    </div>
                    {user.operator_profile.production_line_access && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Line Access:</span>
                        <span className="text-gray-900">{user.operator_profile.production_line_access}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {user.user_type === 'SYSTEM_ADMIN' && user.admin_profile && (
                <div className="py-3 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Admin Details</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Code:</span>
                      <span className="text-gray-900">{user.admin_profile.admin_code}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Access Level:</span>
                      <span className="text-gray-900">{user.admin_profile.access_level}</span>
                    </div>
                    {user.admin_profile.permissions && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Permissions:</span>
                        <span className="text-gray-900">{user.admin_profile.permissions.length}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="pt-3 border-t border-gray-200">
                <Button
                  onClick={() => {
                    setIsOpen(false);
                    onLogout();
                  }}
                  disabled={isLoggingOut}
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2 hover:bg-red-50 hover:border-red-200 hover:text-red-700"
                >
                  {isLoggingOut ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent"></div>
                      Logging out...
                    </>
                  ) : (
                    <>
                      <LogOut className="w-4 h-4" />
                      Logout
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <style jsx>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

// Main App Header Component
export default function AppHeader({ 
  title, 
  subtitle, 
  showBackButton = false, 
  onBack,
  className = "" 
}) {
  const { user, logout, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      const result = await logout();
      if (result.success) {
        toast.success('Logged out successfully');
      } else {
        toast.error('Logout failed: ' + result.error);
      }
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Logout failed');
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Don't show header if user is not authenticated
  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <header className={cn(
      "absolute top-4 right-4 z-30 mb-4",
      className
    )}>
      {/* Desktop User Menu */}
      <div className="hidden sm:flex items-center gap-3">
        <LanguageToggle variant="button" />
        <UserProfileDropdown
          user={user}
          onLogout={handleLogout}
          isLoggingOut={isLoggingOut}
        />
      </div>

      {/* Mobile Menu Button */}
      <div className="sm:hidden">
        <Button
          variant="ghost"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="bg-white/90 hover:bg-white text-gray-800 shadow-lg border"
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </Button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="sm:hidden absolute top-full right-0 mt-2 animate-[slideDown_0.2s_ease-out]">
          <Card className="w-80 shadow-lg border-0">
            <CardContent className="p-4">
              <div className="space-y-4">
                {/* User Info */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{user.first_name} {user.last_name}</p>
                    <p className="text-sm text-gray-600">{user.user_type.replace('_', ' ')}</p>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600">Status</p>
                    <p className="text-sm font-medium text-gray-900">
                      {user.is_active ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600">Role</p>
                    <p className="text-sm font-medium text-gray-900">
                      {user.user_type.replace('_', ' ')}
                    </p>
                  </div>
                </div>

                {/* Language Toggle */}
                <div className="flex justify-center">
                  <LanguageToggle variant="dropdown" className="w-full" />
                </div>

                {/* Logout Button */}
                <Button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleLogout();
                  }}
                  disabled={isLoggingOut}
                  variant="outline"
                  className="w-full hover:bg-red-50 hover:border-red-200 hover:text-red-700"
                >
                  {isLoggingOut ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent mr-2"></div>
                      Logging out...
                    </>
                  ) : (
                    <>
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <style jsx>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </header>
  );
}